import React, { useState, useEffect, useRef, useMemo } from "react";
import { Grid } from "@mui/material";
import clsx from "clsx";
import { FilterMonthlyFeeSearch } from "@icoach/accountBook/FilterMonthlyFeeTemplate";
import { useAccountBookContext } from "@icoach/accountBook/components/AccountBookContext";
import { initBookmark } from "@icoach/members/staticData";
import { Card, Form, Button, SelectField, MenuItem, Stack } from "@curves/ui-base";
import useMemberFilterApi from "@apis/useMemberFilterApi";
import useMemberFilterExportApi from "@apis/useMemberFilterExportApi";
import useExportActionDialog from "@util/hook/useExportActionDialog";
import { securityMaskFilterTypeMap } from "@util/hook/usePrivacyToggle";
import { isEmpty, isEqual, objectToQueryString, paramsToObject, getSize } from "@util/utils";
import { ACCOUNT_BOOK_OVERVIEW_FILTER } from "@icoach/router/routerPath";
import { useHistory } from "react-router-dom";

const numberParamKeys = ["pageIndex", "pageSize", "sortBy"];

const AccountBookOverviewFilterSearchBar = React.memo(
	React.forwardRef((props, ref) => {
		const { className: classNameProps, targetType: quickType } = props;
		const history = useHistory();
		const {
			sourceData,
			setSourceData,
			setRowsLabelType,
			setSortOption,
			bookmark,
			setBookmark,
			setBookmarkNum,
			setBookmarkSortBy,
			setRefresh,
			resource,
			setResource,
			reSetCheckedItems,
			doManualScrollTo,
		} = useAccountBookContext();
		const { overdraftOrderByOption: debtSortOption = [], monthFeeOrderByOption: monthFeeSortOption = [] } = resource;
		const { getMemberFilterOptionsApi, getOverdraftFilterDataApi, getMonthFeeFilterDataApi } = useMemberFilterApi();
		const { postMemberFilterOverdraftApi, postMemberFilterMonthFeeApi } = useMemberFilterExportApi();
		const [paramsData, setParamsData] = useState({}); // 初始預設搜尋條件使用,其他狀況下無用
		const [sortStep, setSortStep] = useState("");
		const [isMounted, setMounted] = useState(false);
		const searchPanelsRef = useRef(null);
		const preloadRef = useRef(false);
		const prevFilterTypeRef = useRef(quickType);
		const { showExportConfirmDialog, showExportPolicyDialog } = useExportActionDialog();

		const parseQueryStringToParams = () => {
			const { location } = history;
			return paramsToObject(location.search, numberParamKeys);
		};

		const getParams = (targetPage = {}) => {
			let params = { ...targetPage };
			if (searchPanelsRef && searchPanelsRef.current) {
				Object.assign(params, {
					...searchPanelsRef.current.getResult(),
				});
			}
			return params;
		};

		const handleTypeChange = (dom, { props }) => {
			history.push(ACCOUNT_BOOK_OVERVIEW_FILTER(props.value));
			preloadRef.current = false;

			setSourceData({ list: [] });
		};

		const handleSearchClick = () => {
			reSetCheckedItems();
			const params = getParams(initBookmark(bookmark?.sortBy));
			setBookmarkNum(1);
			doSearchApi(params);
			preloadRef.current = true; // 防止切換名單後無法更多與排序
		};

		const handleExcelOnClick = async () => {
			await showExportConfirmDialog(getParams(initBookmark(bookmark?.sortBy)), params => doExcelSendEmailApi(params));
			preloadRef.current = true; // 防止切換名單後無法更多與排序
		};

		const doMemberFilterOptionsApi = async () => {
			const resp = await getMemberFilterOptionsApi();
			if (resp) {
				setResource(resp);
			}

			setMounted(true);
			preloadRef.current = true;
		};

		const doSearchApi = async params => {
			const doApi = memberFilterMap[quickType]["doApi"];
			const resp = await doApi(params);
			if (resp) {
				const { list, sortBy, ...pageParams } = resp;
				setSourceData(prev => {
					let targetList = [...list];
					if (!isEqual(params.pageIndex, 1)) {
						targetList = [...(Array.isArray(prev.list) ? prev.list : []), ...targetList];
					}
					return {
						...pageParams,
						list: targetList,
					};
				});

				const url = `${ACCOUNT_BOOK_OVERVIEW_FILTER(quickType)}${objectToQueryString(params)}`;
				history.replace(url);

				preloadRef.current = true;
			}
			if (!isEmpty(paramsData)) {
				setParamsData({}); // 防止換回同類型初始參數
			}
		};

		const doExcelSendEmailApi = async params => {
			const doApi = memberFilterMap[quickType]["excelApi"];
			const resp = await doApi(params);

			showExportPolicyDialog(resp);
		};

		const memberFilterMap = useMemo(
			() => {
				return {
					1: {
						description: "欠費名單",
						rowLabelType: securityMaskFilterTypeMap.ClubMemberOverdraft,
						sortOption: debtSortOption,
						Component: null,
						excelApi: postMemberFilterOverdraftApi,
						doApi: getOverdraftFilterDataApi,
					},
					2: {
						description: "月費收費名單",
						rowLabelType: securityMaskFilterTypeMap.ClubMemberMonthFee,
						sortOption: monthFeeSortOption,
						Component: FilterMonthlyFeeSearch,
						excelApi: postMemberFilterMonthFeeApi,
						doApi: getMonthFeeFilterDataApi,
					},
				};
			},
			// eslint-disable-next-line
			[resource]
		);

		// STEP1. 初始排序 options
		useEffect(
			() => {
				const filterTypeIsSame = isEqual(prevFilterTypeRef.current, quickType);
				const { rowLabelType = 0, sortOption = [] } = memberFilterMap[quickType];

				setRowsLabelType(rowLabelType);
				setSortOption(sortOption);

				if (!filterTypeIsSame) {
					setBookmarkSortBy(1);
					prevFilterTypeRef.current = quickType;
				}
				return () => {
					setSourceData([]);
					setRowsLabelType(0);
				};
			},
			// eslint-disable-next-line
			[quickType]
		);

		useEffect(
			() => {
				const { rowLabelType = [], sortOption = [] } = memberFilterMap[quickType];

				setRowsLabelType(rowLabelType);
				setSortOption(sortOption);

				const params = parseQueryStringToParams();

				if (!isEmpty(params)) {
					setParamsData(params);
					setBookmark({ pageIndex: params.pageIndex, sortBy: params.sortBy });
				} else {
					setBookmarkSortBy(1);
				}

				setSortStep("START");
			},
			// eslint-disable-next-line
			[memberFilterMap]
		);

		// STEP2. 換頁
		useEffect(
			() => {
				if (preloadRef.current) {
					const params = getParams(bookmark);
					if (bookmark && !isEqual(bookmark.pageIndex, 1)) {
						doSearchApi(params);
					}
				}
			},
			// eslint-disable-next-line
			[bookmark.pageIndex]
		);

		// STEP3. 變更排序
		useEffect(
			() => {
				if (preloadRef.current && sourceData && !isEmpty(sourceData.list)) {
					const params = getParams(bookmark);
					doSearchApi(params);
				} else if (isEqual(sortStep, "START")) {
					setSortStep("COMPLETE");
				}
			},
			// eslint-disable-next-line
			[bookmark.sortBy, sortStep]
		);

		useEffect(
			() => {
				if (isEqual(sortStep, "COMPLETE")) {
					let params = parseQueryStringToParams();
					if (!(!isEmpty(params) && isEqual(getSize(sourceData), 0))) {
						doManualScrollTo();
					}
				}
			},
			// eslint-disable-next-line
			[sortStep]
		);

		useEffect(
			() => {
				if (isEmpty(resource)) {
					doMemberFilterOptionsApi();
				}
				return () => {
					setResource({});
					setBookmarkSortBy(1);
					setBookmarkNum(1);
				};
			},
			// eslint-disable-next-line
			[]
		);

		setRefresh(() => {
			const params = getParams(bookmark);
			doSearchApi(params);
		});

		const CompPanel = React.useMemo(
			() => {
				if (!isMounted) return null;
				return quickType ? memberFilterMap[quickType]["Component"] : null;
			},
			// eslint-disable-next-line
			[isMounted, quickType]
		);

		return (
			<Card className={clsx(classNameProps)}>
				<Card.CardContent className="px-4 py-3">
					<Form onSubmit={handleSearchClick}>
						<Grid container spacing={2}>
							<Grid item xs={3}>
								<SelectField label="篩選名單" value={quickType} onChange={handleTypeChange} fullWidth>
									<MenuItem value="1">欠費名單</MenuItem>
									<MenuItem value="2">月費收費名單</MenuItem>
								</SelectField>
							</Grid>
							{isMounted && CompPanel ? <CompPanel ref={searchPanelsRef} resource={resource} /> : null}
							<Grid item xs={3}>
								<Stack direction={"row"} spacing={1} className="pt-4">
									<Button variant="contained" color="secondary" type="submit">
										搜尋
									</Button>
									<Button variant="contained" color="success" onClick={handleExcelOnClick}>
										下載Excel
									</Button>
								</Stack>
							</Grid>
						</Grid>
					</Form>
				</Card.CardContent>
			</Card>
		);
	})
);

export default AccountBookOverviewFilterSearchBar;
